import { DownloadCloudIcon, PackageSearchIcon, TruckIcon, WeightIcon } from 'lucide-react'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { UserPkg } from '@/api/user-package/types/UserPkg'
import ContentIcon from '@/components/icons/content.svg?react'
import DimensionsIcon from '@/components/icons/dimenstions.svg?react'
import FlamableIcon from '@/components/icons/flamable.svg?react'
import LocationIcon from '@/components/icons/location.svg?react'
import PackageActionIcon from '@/components/icons/package-action.svg?react'
import TrackingNumberIcon from '@/components/icons/tracking-number.svg?react'
import { VolumeWeightIcon } from '@/components/icons/VolumeWeightIcon'
import { Button } from '@/components/ui/button'
import { warehouseToTrans } from '@/components/WarehousePreview'
import { getFullUrl } from '@/helpers/files'
import { calcWeight } from '@/helpers/pkgPricing'
import { getShippingMethodLabel, translateProductCategory } from '@/helpers/pkgUtils'
import { cn } from '@/lib/utils'

const DetailField = ({
  label,
  value,
  IconComponent,
  valueProps,
}: {
  label: string
  value: string
  IconComponent: FC<React.SVGProps<SVGSVGElement>>
  valueProps?: React.HTMLAttributes<HTMLParagraphElement>
}) => {
  return (
    <div className='flex items-center justify-between gap-6'>
      <div className='flex items-center gap-1'>
        <IconComponent className='text-text-secondary size-[18px] shrink-0' />
        <p className='text-text-secondary text-xs'>{label}</p>
      </div>
      <p {...(valueProps || {})} className={cn('max-w-40 text-end text-xs', valueProps?.className)}>
        {value}
      </p>
    </div>
  )
}
interface Props {
  pkg: UserPkg
}
export default function ClientPkgDetails({ pkg }: Props) {
  const { t } = useTranslation()
  return (
    <div className='flex w-full flex-col items-center'>
      <div className='mb-6 flex flex-col items-center'>
        <PackageActionIcon />
        <h3 className='text-text-primary font-medium'>{t('pkg.detailsTitle')}</h3>
      </div>

      <div className='w-full space-y-4'>
        {pkg.isShipped && (
          <>
            <DetailField
              label={t('pkg.shippingMethodLabel')}
              value={getShippingMethodLabel(pkg.shippingMethod, t)}
              IconComponent={TruckIcon}
            />
            <DetailField label={t('pkg.trackingNumber')} value={pkg.toTrackingNumber} IconComponent={PackageSearchIcon} />
          </>
        )}
        <DetailField
          label={t('pkg.localTrackingNumber')}
          value={pkg.fromTrackingNumber}
          IconComponent={TrackingNumberIcon}
          valueProps={{ className: 'max-w-[120px] text-wrap wrap-anywhere text-left', dir: 'ltr', title: pkg.fromTrackingNumber }}
        />

        <DetailField label={t('pkg.shippingCountry')} value={t(warehouseToTrans[pkg.warehouse])} IconComponent={LocationIcon} />
        <DetailField
          label={t('pkg.dimensions')}
          value={`${Math.round(pkg.dimensions.length)}x${Math.round(pkg.dimensions.width)}x${Math.round(pkg.dimensions.height)} ${t('pkg.cm')}`}
          IconComponent={DimensionsIcon}
        />
        <DetailField label={t('pkg.physicalWeight')} value={`${pkg.dimensions.weight} ${t('pkg.kg')}`} IconComponent={WeightIcon} />
        <DetailField
          label={t('pkg.volumeWeight')}
          value={`${calcWeight(pkg.dimensions.length, pkg.dimensions.width, pkg.dimensions.height, pkg.dimensions.weight)} ${t('pkg.kg')}`}
          IconComponent={VolumeWeightIcon}
        />
        <DetailField label={t('pkg.flammableItems')} value={pkg.isFlammable ? t('pkg.yes') : t('pkg.no')} IconComponent={FlamableIcon} />
        <DetailField
          label={t('pkg.itemsInPackage')}
          value={pkg.contents
            .map(c => `${c.isSet ? 'Set of' : ''} ${c.quantity} ${translateProductCategory(c.productCategory, t)}`.trim())
            .join(', ')}
          IconComponent={ContentIcon}
        />
        {pkg.isShipped && pkg.buyingReciptKey && (
          <div className='flex items-center justify-between gap-6'>
            <div className='flex items-center gap-1'>
              <DownloadCloudIcon className='text-text-secondary size-[18px]' />
              <p className='text-text-secondary text-xs'>{t('pkg.purchaseInvoice')}</p>
            </div>
            <a href={getFullUrl(pkg.buyingReciptKey)} target='_blank' rel='noopener noreferrer'>
              <Button variant='link' size='sm'>
                {t('pkg.download')}
              </Button>
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
